// Shared form-building primitives used by every VGWS requisition form.
import { Paperclip, PlusCircle } from "lucide-react";

export function Section({ numeral, title, children, testid }) {
  return (
    <section className="sheet p-5 sm:p-7" data-testid={testid || `section-${numeral}`}>
      <div className="flex items-baseline gap-3 mb-5">
        <span className="numeral text-lg tracking-[0.2em]">{numeral}</span>
        <h2 className="font-serif text-xl font-semibold text-[#1f2320]">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
        {label}
        {required && <span className="text-[#b3261e]"> *</span>}
        {hint && <span className="ml-2 text-xs font-normal text-[#6e7770]">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

export function RadioGroup({ name, value, onChange, options, testid, disabled }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value;
        const lbl = typeof opt === "string" ? opt : opt.label;
        const selected = value === val;
        return (
          <label
            key={val}
            className={`px-4 py-2 rounded-md border cursor-pointer text-sm font-medium transition-all ${
              selected
                ? "bg-[#7a1f2b] text-white border-[#5a1520]"
                : "bg-white text-[#7a1f2b] border-[#e2d7c3] hover:border-[#7a1f2b]"
            } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
          >
            <input
              type="radio" className="hidden" name={name} value={val}
              checked={selected}
              onChange={(e) => onChange(e.target.value)}
              data-testid={testid ? `${testid}-${val}` : undefined}
            />
            {lbl}
          </label>
        );
      })}
    </div>
  );
}

export function CheckboxGroup({ value = [], onChange, options, testid }) {
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value;
        const lbl = typeof opt === "string" ? opt : opt.label;
        const selected = value.includes(val);
        return (
          <button
            key={val}
            type="button"
            onClick={() => toggle(val)}
            className={`px-3 py-2 rounded-md border text-sm font-medium transition-all ${
              selected
                ? "bg-[#2d6b6b] text-white border-[#2d6b6b]"
                : "bg-white text-[#2d6b6b] border-[#e2d7c3] hover:border-[#2d6b6b]"
            }`}
            data-testid={testid ? `${testid}-${val}` : undefined}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

export function FileField({ label, fieldName, accept, attachments, onChange, disabled, testid, optional }) {
  const existing = attachments.find((a) => a.field_name === fieldName);
  return (
    <div>
      <div className="block text-sm font-medium text-[#1f2320] mb-1.5">
        {label}
        {optional && <span className="ml-2 text-xs font-normal text-[#6e7770]">Optional</span>}
      </div>
      <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-[#cbbba0] rounded-md bg-white cursor-pointer hover:border-[#7a1f2b] transition-colors" data-testid={testid}>
        <Paperclip size={16} className="text-[#7a1f2b]" />
        <span className="text-sm text-[#4a524c]">
          {existing ? `Replace: ${existing.filename}` : "Choose file (max 10 MB)"}
        </span>
        <input type="file" accept={accept} onChange={onChange} disabled={disabled} className="hidden" />
      </label>
      {existing && (
        <div className="mt-1.5 text-xs text-[#2d6b6b] flex items-center gap-2">
          <span>✔ {existing.filename}</span>
          <span className="text-[#6e7770]">({Math.round(existing.size / 1024)} KB)</span>
        </div>
      )}
    </div>
  );
}

export function TopNotice({ children }) {
  return (
    <div className="top-notice mt-4 px-4 py-3 text-sm" data-testid="top-notice">
      {children}
    </div>
  );
}

export function AddRowButton({ onClick, count, testid }) {
  return (
    <div className="mt-2 flex items-center gap-3">
      <button type="button" className="btn-ghost" onClick={onClick} data-testid={testid}>
        <PlusCircle size={16} /> Add row
      </button>
      <span className="text-xs text-[#6e7770]">{count} row(s)</span>
    </div>
  );
}

/** Applicant / SPOC block — Section I by default */
export function ApplicantSpocSection({ numeral = "I", form, setField }) {
  return (
    <Section numeral={numeral} title="Applicant / SPOC details">
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Name of Applicant / SPOC" required>
          <input className="input-vgws" required value={form.staffName || ""}
                 onChange={(e) => setField("staffName", e.target.value)}
                 data-testid="field-staffName" />
        </Field>
        <Field label="Mail ID of Applicant / SPOC" required>
          <input type="email" className="input-vgws" required value={form.staffEmail || ""}
                 onChange={(e) => setField("staffEmail", e.target.value)}
                 data-testid="field-staffEmail" />
        </Field>
        <Field label="Contact Number" required>
          <input className="input-vgws" required placeholder="10-digit mobile"
                 value={form.staffContact || ""}
                 onChange={(e) => setField("staffContact", e.target.value)}
                 data-testid="field-staffContact" />
        </Field>
      </div>
    </Section>
  );
}

/** Requested-by block — main dept / sub dept / HOD */
export function RequestedBySection({ numeral = "II", form, setField, depts, hods, subDepts, hideHod = false, hodOptional = false }) {
  return (
    <Section numeral={numeral} title="Requested by">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Main department" required>
          <select
            className="input-vgws" required value={form.mainDept || ""}
            onChange={(e) => { setField("mainDept", e.target.value); setField("subDept", ""); }}
            data-testid="field-mainDept"
          >
            <option value="" disabled>Select department</option>
            {depts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Sub department" required>
          <select
            className="input-vgws" required disabled={!subDepts.length}
            value={form.subDept || ""} onChange={(e) => setField("subDept", e.target.value)}
            data-testid="field-subDept"
          >
            <option value="" disabled>
              {subDepts.length ? "Select sub-department" : "Select main department first"}
            </option>
            {subDepts.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      {!hideHod && (
        <Field label="Select the respective HOD for consent & approval" required={!hodOptional}>
          <select
            className={`input-vgws ${hodOptional ? "opacity-70" : ""}`}
            required={!hodOptional}
            value={form.hodApproval || ""}
            onChange={(e) => setField("hodApproval", e.target.value)}
            data-testid="field-hodApproval"
          >
            <option value="">{hodOptional ? "Optional — HOD will be skipped" : "Select HOD"}</option>
            {hods.map((h) => (
              <option key={h.email} value={h.email}>
                {((h.departments || []).join(", ") || "—") + " — " + h.name}
              </option>
            ))}
          </select>
          {hodOptional ? (
            <div className="mt-1 text-[11px] leading-snug text-[#2d6b6b]"
                  data-testid="hod-self-skip-hint">
              HOD approval will be skipped based on your self-declaration.
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-red-600 leading-snug"
                  data-testid="hod-select-hint">
              If the form is Material, Service, Medical, or Safety-Security,
              then HOD can select the HOD of the respective department.
            </div>
          )}
        </Field>
      )}
    </Section>
  );
}

/** Phase 8.3 — Self-skip radios (all forms except Guest).
 *  Owns two mutex Yes/No radios that write into `form.is_hod_self` /
 *  `form.is_admin1_self`. When Admin-1 → also renders optional
 *  cost + remark inputs. Auto-defaults on mount from the logged-in
 *  user's role: hod → is_hod_self=true, admin1/admin1_alt → is_admin1_self=true.
 *  The parent form owns the state; this component is pure UI.
 */
export function SelfSkipSection({ numeral = "II·a", form, setField, userRole,
                                  hideAdmin1 = false }) {
  // Mount-time auto-default. Only sets when both fields are undefined so
  // drafts / re-edits are never overridden.
  if (typeof window !== "undefined" && !form.__selfSkipDefaulted) {
    if (form.is_hod_self === undefined && form.is_admin1_self === undefined) {
      if (userRole === "hod") {
        setField("is_hod_self", true);
      } else if ((userRole === "admin1" || userRole === "admin1_alt") && !hideAdmin1) {
        setField("is_admin1_self", true);
      } else {
        setField("is_hod_self", false);
        if (!hideAdmin1) setField("is_admin1_self", false);
      }
    }
    setField("__selfSkipDefaulted", true);
  }

  const isHodYes = form.is_hod_self === true;
  const isAdminYes = !hideAdmin1 && form.is_admin1_self === true;

  const pickHod = (v) => {
    setField("is_hod_self", v);
    if (v === true && !hideAdmin1) setField("is_admin1_self", false);
  };
  const pickAdmin = (v) => {
    setField("is_admin1_self", v);
    if (v === true) setField("is_hod_self", false);
  };

  return (
    <Section numeral={numeral} title="Approver self-declaration"
             testid="section-self-skip">
      <div className="text-[13px] leading-relaxed text-[#4a524c] -mt-2 mb-1">
        {hideAdmin1
          ? "If you are the HOD for this request, declare so below. The HOD approval stage will be auto-skipped and audited."
          : "If you are the HOD or Admin-1 for this request, declare so below. The corresponding approval stage will be auto-skipped and audited."}
      </div>
      <div className={`grid gap-4 ${hideAdmin1 ? "" : "sm:grid-cols-2"}`}>
        <Field label="Are you a HOD?">
          <div className="flex gap-2" data-testid="field-is_hod_self">
            <YesNoPill selected={isHodYes} disabled={isAdminYes} value onClick={() => pickHod(true)}
                       testid="is_hod_self-yes" />
            <YesNoPill selected={form.is_hod_self === false} value={false}
                       onClick={() => pickHod(false)} testid="is_hod_self-no" />
          </div>
        </Field>
        {!hideAdmin1 && (
          <Field label="Are you an Admin-1 (Bursar/CAO)?">
            <div className="flex gap-2" data-testid="field-is_admin1_self">
              <YesNoPill selected={isAdminYes} disabled={isHodYes} value onClick={() => pickAdmin(true)}
                         testid="is_admin1_self-yes" />
              <YesNoPill selected={form.is_admin1_self === false} value={false}
                         onClick={() => pickAdmin(false)} testid="is_admin1_self-no" />
            </div>
          </Field>
        )}
      </div>
      {isAdminYes && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Cost" hint="Optional — alphanumeric, e.g. Rs 12,500 approx">
            <input className="input-vgws" value={form.admin1_self_cost || ""}
                    onChange={(e) => setField("admin1_self_cost", e.target.value)}
                    data-testid="field-admin1_self_cost" />
          </Field>
          <Field label="Remark" hint="Optional — brief context for auditors">
            <textarea className="input-vgws min-h-[60px]"
                       value={form.admin1_self_remark || ""}
                       onChange={(e) => setField("admin1_self_remark", e.target.value)}
                       data-testid="field-admin1_self_remark" />
          </Field>
        </div>
      )}
    </Section>
  );
}

function YesNoPill({ selected, disabled = false, value, onClick, testid }) {
  return (
    <button type="button"
            disabled={disabled}
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 rounded-md border text-sm font-medium transition-all ${
              selected
                ? "bg-[#7a1f2b] text-white border-[#5a1520]"
                : "bg-white text-[#7a1f2b] border-[#e2d7c3] hover:border-[#7a1f2b]"
            } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
      {value ? "Yes" : "No"}
    </button>
  );
}

/** Phase 8.3 — helper: is the HOD field optional right now? */
export function isHodDropdownOptional(form) {
  return form?.is_hod_self === true || form?.is_admin1_self === true;
}

/** Phase 8.3 — helper: sanitize form_data before submit to strip our
 *  transient __selfSkipDefaulted marker and coerce radios to booleans. */
export function stripSelfSkipTransients(fd) {
  const { __selfSkipDefaulted, ...rest } = fd || {};
  return {
    ...rest,
    is_hod_self: rest.is_hod_self === true,
    is_admin1_self: rest.is_admin1_self === true,
  };
}

export function makeUploader(api, setAttachments, setUploading, toast, formatApiError) {
  return async (fieldName, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File larger than 10 MB. Please compress and retry.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [
        ...prev.filter((a) => a.field_name !== fieldName),
        { field_name: fieldName, filename: data.filename, url: data.url, size: data.size, uploaded_at: new Date().toISOString() },
      ]);
      toast.success(`${file.name} uploaded.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
}

/* -------- Phase 16 (item 10) — Re-submit form section -------- */
import { useEffect as _useEffect, useState as _useState } from "react";
import _api from "@/lib/api";

/**
 * Phase 16 item 10 — renders a Yes/No radio at the top of every form. On Yes,
 * shows a searchable dropdown of the applicant's own past requisitions (any
 * form_type, any status). On selection, calls onPrefill(fd, parent_ref_code)
 * with a DEEP COPY of the chosen ticket's form_data (with attachments cleared).
 */
export function ResubmitFromSection({ isResubmit, onToggle, parentRefCode, onSelectHistory }) {
  const [history, setHistory] = _useState([]);
  const [loading, setLoading] = _useState(false);
  const [search, setSearch] = _useState("");
  _useEffect(() => {
    if (!isResubmit || history.length) return;
    setLoading(true);
    _api.get("/requisitions/mine/history?limit=100")
      .then((r) => setHistory(r.data?.items || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [isResubmit, history.length]);

  const filtered = history.filter((h) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (h.ref_code || "").toLowerCase().includes(q)
      || (h.form_type || "").toLowerCase().includes(q)
      || (h.main_dept || "").toLowerCase().includes(q)
      || (h.purpose || "").toLowerCase().includes(q);
  });

  const onPick = async (ref) => {
    try {
      const { data } = await _api.get(`/requisitions/${ref}`);
      const fd = JSON.parse(JSON.stringify(data.form_data || {}));
      // Strip transients: attachments, admin_status, workflow markers.
      delete fd.attachments;
      if (Array.isArray(fd.items)) fd.items = fd.items.map((it) => {
        const { admin_status, ...rest } = it; return rest;
      });
      if (Array.isArray(fd.services)) fd.services = fd.services.map((it) => {
        const { admin_status, ...rest } = it; return rest;
      });
      onSelectHistory && onSelectHistory(fd, data.ref_code);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("resubmit-history fetch failed", e);
    }
  };

  return (
    <Section numeral="0·A" title="Re-submission" testid="section-resubmit-from">
      <Field label='Is this a re-submission of a previous form?'>
        <div className="flex gap-3" data-testid="field-is-resubmit">
          {[["yes", "Yes"], ["no", "No"]].map(([v, lbl]) => (
            <label key={v} className={`px-4 py-2 rounded-md border cursor-pointer text-sm font-medium ${
              (isResubmit ? "yes" : "no") === v
                ? "bg-[#7a1f2b] text-white border-[#5a1520]"
                : "bg-white text-[#7a1f2b] border-[#e2d7c3]"}`}>
              <input type="radio" className="hidden" name="is_resubmit" value={v}
                     checked={(isResubmit ? "yes" : "no") === v}
                     onChange={() => onToggle(v === "yes")}
                     data-testid={`is-resubmit-${v}`} />
              {lbl}
            </label>
          ))}
        </div>
      </Field>
      {isResubmit && (
        <div className="space-y-2">
          <div className="text-xs text-[#6e7770]">
            Pick one of your past submissions. The form will be prefilled with its data
            (attachments cleared). The original ticket is preserved.
          </div>
          <input
            type="text"
            className="input-vgws"
            placeholder="Search by ref, form type, department, purpose…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="resubmit-search"
          />
          <div className="max-h-56 overflow-auto rounded-md border border-[#e2d7c3] bg-white"
               data-testid="resubmit-history-list">
            {loading ? (
              <div className="p-3 text-xs text-[#6e7770]">Loading history…</div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-xs text-[#6e7770]">No matching past requisitions.</div>
            ) : (
              filtered.slice(0, 50).map((h) => (
                <button key={h.ref_code}
                        type="button"
                        onClick={() => onPick(h.ref_code)}
                        className={`w-full text-left px-3 py-2 border-b border-[#efe5cf] hover:bg-[#faf5eb] transition-colors ${
                          parentRefCode === h.ref_code ? "bg-[#faf5eb]" : ""}`}
                        data-testid={`resubmit-history-item-${h.ref_code}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-[#7a1f2b] text-sm">{h.ref_code}</span>
                    <span className="text-[10px] uppercase tracking-widest text-[#6e7770]">
                      {h.form_type} · {h.status}
                    </span>
                  </div>
                  {h.main_dept && (
                    <div className="text-[11px] text-[#4a524c] truncate">{h.main_dept}</div>
                  )}
                  {h.purpose && (
                    <div className="text-[11px] text-[#6e7770] truncate">{h.purpose}</div>
                  )}
                </button>
              ))
            )}
          </div>
          {parentRefCode && (
            <div className="text-xs text-[#2d6b6b] font-medium"
                 data-testid="resubmit-selected-pill">
              Prefilled from <strong>{parentRefCode}</strong>.
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/**
 * Phase 16 (item 3) — Parse a leading numeric prefix from any cell.
 * Returns { value, parseable, raw }. Handles alphanumeric qty (e.g. "5 kg" → 5).
 * For pure numeric strings, returns the number directly.
 */
export function parseNumericPrefix(v) {
  if (v === null || v === undefined) return { value: 0, parseable: false, raw: "" };
  const s = String(v).trim();
  if (!s) return { value: 0, parseable: false, raw: s };
  const m = s.match(/^-?\s*(\d+(?:[.,]\d+)?)/);
  if (!m) return { value: 0, parseable: false, raw: s };
  const n = parseFloat(m[1].replace(",", "."));
  if (isNaN(n)) return { value: 0, parseable: false, raw: s };
  return { value: n, parseable: true, raw: s };
}

/**
 * Phase 16 (item 3) — Compute grand total of Approx Cost from item list.
 * Returns { total, hasUnparseable }.
 */
export function computeGrandTotal(items = []) {
  let total = 0;
  let hasUnparseable = false;
  for (const it of items || []) {
    if (!it) continue;
    // Skip empty rows (no name AND no cost).
    if (!String(it.name || "").trim() && !String(it.cost || "").trim()) continue;
    const { value, parseable } = parseNumericPrefix(it.cost);
    if (parseable) total += value;
    else if (String(it.cost || "").trim()) hasUnparseable = true;
  }
  return { total, hasUnparseable };
}
